import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { produtosApi } from '@/lib/api';
import type { Produto } from '@/types';

export function useProdutos(params?: { ativo?: boolean; categoriaId?: string; busca?: string }) {
  // Filtrar parâmetros vazios
  const filteredParams = params ? Object.fromEntries(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
  ) : undefined;

  return useQuery({
    queryKey: ['produtos', filteredParams],
    queryFn: async () => {
      const response = await produtosApi.listar(filteredParams as any);
      // Backend retorna { produtos, paginacao } ou array direto
      const data = response.data;
      // Suporte para resposta paginada { success, data, meta } ou array direto
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as Produto[];
      }
      return (Array.isArray(data) ? data : []) as Produto[];
    },
  });
}

export function useProdutosPublico(params?: { categoriaId?: string; busca?: string }) {
  return useQuery({
    queryKey: ['produtos-publico', params],
    queryFn: async () => {
      const response = await produtosApi.listarPublico({ ...params, ativo: true });
      const data = response.data;
      // Trata resposta paginada { success, data, meta }
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as Produto[];
      }
      return (Array.isArray(data) ? data : []) as Produto[];
    },
  });
}

export function useProdutosSalaSecreta(params?: { categoriaId?: string; busca?: string }) {
  return useQuery({
    queryKey: ['produtos-sala-secreta', params],
    queryFn: async () => {
      const response = await produtosApi.listarSalaSecreta(params);
      const data = response.data;
      // Trata resposta paginada { success, data, meta }
      if (data && typeof data === 'object' && 'data' in data && Array.isArray(data.data)) {
        return data.data as Produto[];
      }
      return (Array.isArray(data) ? data : []) as Produto[];
    },
  });
}

export function useProduto(id: string) {
  return useQuery({
    queryKey: ['produto', id],
    queryFn: async () => {
      const response = await produtosApi.buscar(id);
      // Trata resposta padronizada { success, data: { ... } } ou objeto direto
      const data = response.data;
      if (data && typeof data === 'object' && 'success' in data && data.data) {
        return data.data as Produto;
      }
      return data as Produto;
    },
    enabled: !!id,
  });
}


export function useProdutoPorSlug(slug: string) {
  return useQuery({
    queryKey: ['produto-slug', slug],
    queryFn: async () => {
      const response = await produtosApi.buscarPorSlug(slug);
      return (response.data.data || response.data) as Produto;
    },
    enabled: !!slug,
  });
}

export function useCriarProduto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: any) => produtosApi.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
    },
  });
}

export function useAtualizarProduto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      produtosApi.atualizar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      queryClient.invalidateQueries({ queryKey: ['produto', id] });
    },
  });
}

export function useDeletarProduto() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => produtosApi.deletar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
    },
  });
}

export function useBulkDeletarProdutos() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ids: string[]) => produtosApi.bulkDeletar(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
    },
  });
}
